import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ExclamationCircleOutlined,
  LoginOutlined,
  PlusSquareOutlined,
} from '@ant-design/icons';
import { Button, Spin } from 'antd';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import AdFrame from 'components/UI/AdFrame';
import type Ad from 'stores/Ad';
import type Creative from 'stores/Creative';
import { ifDimensions, ifTemplate } from 'stores/DynamicProperties/casts';
import type Template from 'stores/Template';
import { AdEvents } from 'types/AdEvents';
import { PanelViews } from 'types/PanelViews';
import { isTerminalProcessingStatus } from 'types/ProcessingStatus';
import { UploadStates } from 'types/UploadStates';
import { arrayCount } from 'utils/Array';
import { isDeepEqual } from 'utils/Compare';
import { stripSVGDataInPlace } from 'utils/SvgUtils';

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const largeIconStyle = css`
  font-size: 135px;
  margin-bottom: -20px;
  color: #dddee0;
`;

const ArrowRightIcon = styled(ArrowRightOutlined)`
  ${largeIconStyle}
`;

const DragTargetIcon = styled(LoginOutlined)`
  transform: rotate(90deg);
  ${largeIconStyle}
`;

const ExclamationCircleIcon = styled(ExclamationCircleOutlined)`
  ${largeIconStyle}
`;

const PlusSquareIcon = styled(PlusSquareOutlined)`
  ${largeIconStyle}
`;

const ArrowLeftIcon = styled(ArrowLeftOutlined)`
  ${largeIconStyle}
`;

const AdContainer = styled.div`
  overflow: auto;
  height: calc(100% - 50px);
  width: 100%;
`;

const EmptyPlaceholder = styled.div`
  width: 100%;
  height: calc(100% - 50px);
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-wrap: wrap;
  flex-flow: column;
  line-height: 40px;
`;

interface ValidAdViewAttributes {
  valid: true;
  width: number;
  height: number;
  src: string;
}

type AdViewAttributesReason =
  | 'no-ad'
  | 'no-template'
  | 'no-dimensions'
  | 'bad-template-url';

interface InvalidAdViewAttributes {
  valid: false;
  reason: AdViewAttributesReason;
}

type AdViewAttributes = ValidAdViewAttributes | InvalidAdViewAttributes;

/**
 * Pure: `sessionToken` is passed in rather than read from the clock here.
 *
 * That is load-bearing, not tidiness. This runs during render, and the
 * token lands in the iframe's `src` - so deriving it from Date.now() would
 * hand the iframe a new src on every render, and this is an observer that
 * re-renders whenever anything it reads changes. The frame would reload
 * continuously.  Reloading is a decision, so the token only moves when
 * something calls requestReload.
 *
 * `session` is a cache-buster - getHostedURL() returns a stable
 * prefix/entry path with no hash, so it is the only thing that can force a
 * refetch of a re-uploaded template. It stays a Date.now() millisecond
 * value because the template bundle in the iframe can read its own query
 * string, and those bundles are user-uploaded ZIPs we cannot audit.
 * Nothing in this repo reads it, but that is not the same as nothing
 * reading it.
 */
function getAdViewAttributes(
  ad: Ad | undefined,
  sessionToken: number
): AdViewAttributes {
  if (!ad) return { valid: false, reason: 'no-ad' };

  const templateProperty = ifTemplate(ad.properties.getChild('template'));
  const templateId = templateProperty?.templateId;
  if (!templateProperty || !templateId) {
    return { valid: false, reason: 'no-template' };
  }

  const dimensionsProperty = ifDimensions(ad.properties.getChild('dimensions'));
  const dimensions = dimensionsProperty?.getTuple();
  if (!dimensions) return { valid: false, reason: 'no-dimensions' };

  const [width, height] = dimensions;

  let src: string;
  try {
    // Inside the try because building the URL is what can fail. Undefined
    // would mean the template no longer resolves, which templateId has
    // already ruled out; it gets the same treatment as an unparseable URL.
    const srcUrl = new URL(templateProperty.getHostedURL() ?? '');
    srcUrl.searchParams.set('isAdHubPreview', 'true');
    srcUrl.searchParams.set('session', `${sessionToken}`);
    src = srcUrl.toString();
  } catch (urlError) {
    // Warned rather than carried on the result: the reason is enough to
    // pick a placeholder, and nothing was ever going to render the error
    // object.  A template whose hosted URL will not parse is a data
    // problem worth seeing in the console.
    console.warn(
      `[AdViewFrame] template ${templateId} has an unparseable hosted URL`,
      urlError
    );
    return { valid: false, reason: 'bad-template-url' };
  }

  return {
    valid: true,
    width,
    height,
    src,
  };
}

export interface AdViewFrameProps {
  creative?: Creative | null | undefined;
  fileDragging?: boolean;
}

interface AdPlaceholderProps {
  mounted: boolean;
  templateCount: number;
  fileDragging: boolean;
  templateUploading: boolean;
  onUploadTemplateClick: () => void;
  processingTemplateCount: number;
  adCount: number;

  // Not optional: this only renders when getAdViewAttributes returned
  // invalid, so there is always a reason. That is what lets the switch
  // below be total.
  adViewAttributesReason: AdViewAttributesReason;
}

function AdPlaceholder({
  mounted,
  templateCount,
  fileDragging,
  templateUploading,
  onUploadTemplateClick,
  processingTemplateCount,
  adCount,
  adViewAttributesReason,
}: AdPlaceholderProps) {
  if (!mounted) {
    return (
      <EmptyPlaceholder>
        <Spin />
      </EmptyPlaceholder>
    );
  }

  if (templateCount < 1) {
    if (fileDragging) {
      return (
        <EmptyPlaceholder>
          <ArrowRightIcon />
          <br />
          Drop your template onto the templates panel.
        </EmptyPlaceholder>
      );
    }

    return (
      <EmptyPlaceholder>
        <DragTargetIcon />
        <br />
        Drag your template ZIP file here.
        <br />
        <Button
          disabled={templateUploading}
          loading={templateUploading}
          onClick={onUploadTemplateClick}
        >
          Upload Template
        </Button>
      </EmptyPlaceholder>
    );
  }

  if (templateCount === processingTemplateCount) {
    // templates are present, but none are ready
    return (
      <EmptyPlaceholder>
        <ExclamationCircleIcon />
        <br />
        <Button disabled loading>
          Upload Template
        </Button>{' '}
        to get started.
      </EmptyPlaceholder>
    );
  }

  if (adCount === 0) {
    return (
      <EmptyPlaceholder>
        <PlusSquareIcon />
        <br />
        Click 'New' to create a new ad.
      </EmptyPlaceholder>
    );
  }

  // The checks above are about the creative as a whole and deliberately
  // come first - with no ads at all, "click New" beats "select an ad".
  // From here down it is about the selected ad, which is what the reason
  // describes.
  switch (adViewAttributesReason) {
    case 'no-ad':
      return (
        <EmptyPlaceholder>
          <ArrowLeftIcon />
          <br />
          Select an ad on the left panel to get started.
        </EmptyPlaceholder>
      );

    case 'no-template':
      return (
        <EmptyPlaceholder>
          <ExclamationCircleIcon />
          <br />
          The current selected ad is missing a template.
          <br />
          Select one in the ad properties.
        </EmptyPlaceholder>
      );

    case 'no-dimensions':
      return (
        <EmptyPlaceholder>
          <ExclamationCircleIcon />
          <br />
          The current selected ad is missing valid dimensions.
          <br />
          Set a width and height in the ad properties.
        </EmptyPlaceholder>
      );

    case 'bad-template-url':
      return (
        <EmptyPlaceholder>
          <ExclamationCircleIcon />
          <br />
          This ad's template URL is invalid. Contact a developer.
          <br />
          In the meantime, try re-uploading it, or select a different one.
        </EmptyPlaceholder>
      );

    default: {
      // Note that this looks like an unnecessary alias, but annotating the
      // :never type ensures that tsc will report an error if the above
      // cases are not exhaustive. Previously this function could fall off
      // the end and render nothing at all - which is what
      // 'bad-template-url' did.
      const unhandledReason: never = adViewAttributesReason;
      console.warn(
        '[AdViewFrame] unhandled AdViewAttributesReason',
        unhandledReason
      );
      return null;
    }
  }
}

function AdViewFrame({ creative, fileDragging }: AdViewFrameProps) {
  const templates: ReadonlyArray<Template> = creative?.templates.children ?? [];

  const adCount = creative?.ads.children.length ?? 0;
  const processingTemplateCount = arrayCount(
    templates,
    (t) => !isTerminalProcessingStatus(t.data.status)
  );
  const currentAd = creative?.ads.current;

  // The frame reloads exactly when this changes, because it is what varies
  // the iframe's src.  Math.max keeps it a plausible millisecond timestamp
  // while guaranteeing it moves - two reloads within the same millisecond
  // would otherwise produce an identical src and silently not reload.
  const [sessionToken, setSessionToken] = useState(() => Date.now());
  const requestReload = useCallback(
    () => setSessionToken((previous) => Math.max(Date.now(), previous + 1)),
    []
  );

  // What the frame is currently showing, so a CHANGED event can tell a real
  // change from a no-op. getPreviewData() builds a fresh tree every call, so
  // identity says nothing and this has to be a deep compare.
  const loadedPreviewData = useRef<unknown>(undefined);

  const currentPreviewData = useCallback(() => {
    const data = currentAd?.properties.getPreviewData();
    stripSVGDataInPlace(data);
    return data;
  }, [currentAd]);

  // Re-snapshot whenever the frame (re)loads - which is exactly when the ad or
  // the token changes - so subsequent comparisons are against what is on
  // screen rather than against some earlier state.
  useEffect(() => {
    loadedPreviewData.current = currentPreviewData();
  }, [currentPreviewData, sessionToken]);

  // Reloading the iframe is expensive and visible, so a CHANGED event that did
  // not actually alter the preview data should not cause one. This is what the
  // class called checkHasChanged.
  const requestReloadIfChanged = useCallback(() => {
    if (isDeepEqual(loadedPreviewData.current, currentPreviewData())) return;
    requestReload();
  }, [currentPreviewData, requestReload]);

  const debouncedReload = useRef(NaN);
  const clearDebouncedReload = useCallback(() => {
    if (isFinite(debouncedReload.current)) {
      window.clearTimeout(debouncedReload.current);
      debouncedReload.current = NaN;
    }
  }, []);

  const requestReloadLater = useCallback(() => {
    if (isFinite(debouncedReload.current)) return;
    debouncedReload.current = window.setTimeout(() => {
      debouncedReload.current = NaN;
      requestReloadIfChanged();
    }, 500);
  }, [requestReloadIfChanged]);

  useEffect(() => clearDebouncedReload, [clearDebouncedReload]);

  // Depends on `creative`, not on the uploadState it observes: with the latter
  // in the deps the reaction was torn down and rebuilt by the very change it
  // was watching.
  //
  // Only a completed upload means there is new content to show. Whether it
  // matters to this ad is left to requestReloadIfChanged: an asset's preview
  // data is its URL, so the comparison already finds any asset the ad uses,
  // however deeply nested (SVGText's font, say). The class instead looked the
  // newest asset up among the ad's top-level properties, which missed nested
  // ones and trusted `newest` - a one-second-resolution max over
  // dateModified - to be the upload that had just finished.
  useEffect(
    () =>
      reaction(
        () => creative?.assets.uploadState,
        (uploadState) => {
          if (uploadState === UploadStates.UPLOAD_COMPLETE) {
            requestReloadIfChanged();
          }
        }
      ),
    [creative, requestReloadIfChanged]
  );

  // No ad-activated listener (AdsManager's ACTIVATED event, since removed as
  // unused). `AdsManager.currentId` has an initializer, so becomeObservable()
  // picks it up and `current` is a computed - reading it during render
  // already re-renders this observer on an ad switch. The event was only
  // needed to reload, which this does, and dropping it removed a leak: the
  // inner function's return value was being handed to `when`, which ignores
  // it, so the listener was never actually removed.
  //
  // Skipped on mount: the first render already loads the frame, and bumping
  // the token here would immediately load it a second time.
  const previouslyLoadedAd = useRef(currentAd);
  useEffect(() => {
    if (previouslyLoadedAd.current === currentAd) return;
    previouslyLoadedAd.current = currentAd;
    requestReload();
  }, [currentAd, requestReload]);

  useEffect(() => {
    currentAd?.addEventListener(AdEvents.RELOAD, requestReload);
    return () => {
      currentAd?.removeEventListener(AdEvents.RELOAD, requestReload);
    };
  }, [currentAd, requestReload]);

  useEffect(() => {
    currentAd?.addEventListener(AdEvents.CHANGED, requestReloadLater);
    return () => {
      currentAd?.removeEventListener(AdEvents.CHANGED, requestReloadLater);
      // Drop any pending debounce along with the listener. Keyed on
      // currentAd, so this also fires on an ad switch - where a surviving
      // timer would compare the previous ad's data against the frame that has
      // since reloaded for the new one, and reload it again for nothing.
      clearDebouncedReload();
    };
  }, [currentAd, requestReloadLater, clearDebouncedReload]);

  const viewScale = creative?.viewScale ?? 1;

  const attrs = getAdViewAttributes(currentAd, sessionToken);

  const showUploadTemplateModal = () => {
    if (!creative) return;
    creative.setPanel(PanelViews.TEMPLATES);
    creative.templates.uploadManager.modalVisible = true;
  };

  return (
    <Container>
      {attrs.valid ? (
        <AdContainer>
          <AdFrame
            src={attrs.src}
            width={attrs.width}
            height={attrs.height}
            scale={viewScale}
            iframeName="adframe"
            getDynamicData={() => currentAd?.properties.getPreviewData()}
          />
        </AdContainer>
      ) : (
        <AdPlaceholder
          mounted={!!creative?.mounted}
          templateCount={templates.length}
          fileDragging={!!fileDragging}
          templateUploading={!!creative?.templates.uploadManager.loading}
          onUploadTemplateClick={showUploadTemplateModal}
          processingTemplateCount={processingTemplateCount}
          adCount={adCount}
          adViewAttributesReason={attrs.reason}
        />
      )}
    </Container>
  );
}

export default observer(AdViewFrame);
